import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import { format } from 'date-fns';

// Custom hook for dashboard data with React Query caching
export const useDashboardData = (dateFrom = null, dateTo = null) => {
  const queryKey = ['dashboard', dateFrom, dateTo];
  
  return useQuery({
    queryKey,
    queryFn: async () => {
      let params = '';
      if (dateFrom && dateTo) {
        const fromStr = format(dateFrom, 'yyyy-MM-dd');
        const toStr = format(dateTo, 'yyyy-MM-dd');
        params = `?date_from=${fromStr}&date_to=${toStr}`;
      }
      const response = await api.get(`/dashboard${params}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Custom hook for monthly chart data
export const useMonthlyChartData = (dateFrom = null, dateTo = null) => {
  const queryKey = ['monthlyChart', dateFrom, dateTo];
  
  return useQuery({
    queryKey,
    queryFn: async () => {
      let params = '';
      if (dateFrom && dateTo) {
        const fromStr = format(dateFrom, 'yyyy-MM-dd');
        const toStr = format(dateTo, 'yyyy-MM-dd');
        params = `?date_from=${fromStr}&date_to=${toStr}`;
      }
      const response = await api.get(`/dashboard/monthly-net-rtr-chart${params}`);
      return response.data.data || [];
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });
};

// Custom hook for S&P 500 data
export const useSP500Data = (startDate = null) => {
  const queryKey = ['sp500', startDate];
  
  return useQuery({
    queryKey,
    queryFn: async () => {
      let params = startDate ? `?start_date=${startDate}` : '';
      const response = await api.get(`/sp500-data${params}`);
      return response.data.data || [];
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - S&P data doesn't change often
    cacheTime: 7 * 24 * 60 * 60 * 1000, // 7 days
  });
};

// Custom hook for MCA news
export const useMCANews = () => {
  return useQuery({
    queryKey: ['mcaNews'],
    queryFn: async () => {
      const response = await api.get('/dashboard/mca-news');
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    cacheTime: 24 * 60 * 60 * 1000, // 24 hours
  });
};

// Custom hook for monthly net received from investments
export const useMonthlyNetReceived = () => {
  return useQuery({
    queryKey: ['monthlyNetReceived'],
    queryFn: async () => {
      const response = await api.get('/investments/net-received-by-month');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};
